import React from 'react';
import {motion} from 'framer-motion';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';

const {FiMonitor, FiUsers, FiMail, FiCheck, FiSearch, FiUserPlus} = FiIcons;

const ServiceSelector = ({selectedService, setSelectedService, selectedSubService, setSelectedSubService}) => {
  const services = [
    {
      id: 'website',
      name: 'Website Redesign',
      icon: FiMonitor,
      description: 'Analyze websites for redesign opportunities and create personalized outreach messages.',
      subServices: []
    },
    {
      id: 'onboarding',
      name: 'Client Onboarding',
      icon: FiUsers,
      description: 'Create personalized outreach for onboarding clients to sales platforms.',
      subServices: [
        {id: 'apollo', name: 'Apollo.io'},
        {id: 'instantly', name: 'Instantly.ai'}
      ]
    },
    {
      id: 'cold_email',
      name: 'Cold Email System',
      icon: FiMail,
      description: 'Set up automated cold email systems tailored to client needs.',
      subServices: []
    },
    {
      id: 'aeo',
      name: 'AEO (AI Engine Optimization)',
      icon: FiSearch,
      description: 'Optimize websites for AI search engines and chatbots to improve AI-driven discovery.',
      subServices: []
    },
    {
      id: 'partnership',
      name: 'Partnership',
      icon: FiUserPlus,
      description: 'Partner with agencies and businesses that need a web development team.',
      subServices: []
    }
  ];

  const currentService = services.find(s => s.id === selectedService);
  const hasSubServices = currentService?.subServices?.length > 0;

  return (
    <motion.div
      initial={{opacity: 0}}
      animate={{opacity: 1}}
      className="service-selector"
    >
      <div className="service-header">
        <h2>Select Service</h2>
        <p>Choose which service you want to analyze websites for</p>
      </div>

      <div className="services-grid">
        {services.map((service) => (
          <div
            key={service.id}
            className={`service-card ${selectedService === service.id ? 'selected' : ''}`}
            onClick={() => {
              setSelectedService(service.id);
              // Reset sub-service when changing main service
              if (service.subServices.length > 0) {
                setSelectedSubService(service.subServices[0].id);
              } else {
                setSelectedSubService(null);
              }
            }}
          >
            <div className="service-icon">
              <SafeIcon icon={service.icon} />
            </div>
            <div className="service-info">
              <h3>{service.name}</h3>
              <p>{service.description}</p>
            </div>
            {selectedService === service.id && (
              <div className="selected-indicator">
                <SafeIcon icon={FiCheck} />
              </div>
            )}
          </div>
        ))}
      </div>

      {hasSubServices && (
        <div className="sub-services">
          <h3>Choose Platform</h3>
          <div className="sub-services-options">
            {currentService.subServices.map(subService => (
              <div
                key={subService.id}
                className={`sub-service-option ${selectedSubService === subService.id ? 'selected' : ''}`}
                onClick={() => setSelectedSubService(subService.id)}
              >
                <span>{subService.name}</span>
                {selectedSubService === subService.id && (
                  <SafeIcon icon={FiCheck} className="check-icon" />
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      <style jsx="true">{`
        .service-selector {
          margin-bottom: 1.5rem;
        }
        
        .service-header {
          margin-bottom: 1rem;
        }
        
        .service-header h2 {
          font-size: 1.25rem;
          font-weight: 600;
          margin-bottom: 0.25rem;
        }
        
        .service-header p {
          font-size: 0.875rem;
          color: var(--text-light);
        }
        
        .services-grid {
          display: grid;
          grid-template-columns: repeat(auto-fill, minmax(250px, 1fr));
          gap: 1rem;
          margin-bottom: 1.5rem;
        }
        
        .service-card {
          background-color: white;
          border-radius: 8px;
          padding: 1.25rem;
          display: flex;
          align-items: center;
          gap: 1rem;
          box-shadow: var(--shadow-sm);
          cursor: pointer;
          position: relative;
          border: 2px solid transparent;
          transition: all 0.2s ease;
        }
        
        .service-card:hover {
          box-shadow: var(--shadow-md);
        }
        
        .service-card.selected {
          border-color: var(--primary-color);
          background-color: #f5f3ff;
        }
        
        .service-icon {
          width: 48px;
          height: 48px;
          background-color: #eff6ff;
          border-radius: 12px;
          display: flex;
          align-items: center;
          justify-content: center;
          color: var(--blue-color);
          font-size: 1.5rem;
          flex-shrink: 0;
        }
        
        .selected .service-icon {
          background-color: #e0e7ff;
          color: var(--primary-color);
        }
        
        .service-info {
          flex: 1;
        }
        
        .service-info h3 {
          font-size: 1rem;
          font-weight: 600;
          margin-bottom: 0.25rem;
        }
        
        .service-info p {
          font-size: 0.75rem;
          color: var(--text-light);
          line-height: 1.4;
        }
        
        .selected-indicator {
          position: absolute;
          top: -8px;
          right: -8px;
          width: 24px;
          height: 24px;
          background-color: var(--primary-color);
          border-radius: 50%;
          display: flex;
          align-items: center;
          justify-content: center;
          color: white;
        }
        
        .sub-services {
          background-color: white;
          border-radius: 8px;
          padding: 1.25rem;
          box-shadow: var(--shadow-sm);
        }
        
        .sub-services h3 {
          font-size: 0.875rem;
          font-weight: 600;
          margin-bottom: 1rem;
          color: var(--text-light);
        }
        
        .sub-services-options {
          display: flex;
          gap: 1rem;
        }
        
        .sub-service-option {
          padding: 0.75rem 1.25rem;
          border-radius: 6px;
          background-color: #f9fafb;
          border: 1px solid #e5e7eb;
          cursor: pointer;
          font-size: 0.875rem;
          display: flex;
          align-items: center;
          gap: 0.5rem;
          transition: all 0.2s ease;
        }
        
        .sub-service-option:hover {
          background-color: #f5f3ff;
          border-color: #e0e7ff;
        }
        
        .sub-service-option.selected {
          background-color: #eff6ff;
          border-color: var(--primary-color);
          color: var(--primary-color);
          font-weight: 500;
        }
        
        .check-icon {
          color: var(--primary-color);
        }
        
        @media (max-width: 640px) {
          .services-grid {
            grid-template-columns: 1fr;
          }
          
          .sub-services-options {
            flex-direction: column;
          }
        }
      `}</style>
    </motion.div>
  );
};

export default ServiceSelector;